import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Lock } from 'lucide-react';

const CALCULATORS = [
  { id: 'per-unit-base', label: 'Per-Unit Base Calculator', module: 'M03', desc: 'Input Sbase and Vbase → compute Zbase and Ibase with a sensitivity chart.', route: '/per-unit', section: '#calculators', live: true },
  { id: 'base-change', label: 'Base Change Calculator', module: 'M03', desc: 'Convert Zpu from equipment nameplate base to system base.', route: '/per-unit', section: '#calculators', live: true },
  { id: 'network-builder', label: 'Per-Unit Network Builder', module: 'M03', desc: 'Add transformer and line elements, compute per-unit impedances.', route: '/per-unit', section: '#calculators', live: true },
  { id: 'wye-delta', label: 'Wye/Delta Converter', module: 'M01', desc: 'Enter VLL or VLN → compute all related voltage and current values.', route: '/foundation', live: false },
  { id: 'three-phase-power', label: 'Three-Phase Power Calculator', module: 'M01', desc: 'Input V, I, PF → output P (MW), Q (MVAr), S (MVA).', route: '/foundation', live: false },
  { id: 'phasor-viz', label: 'Phasor Diagram Visualizer', module: 'M01', desc: 'Enter phasor magnitudes and angles, see the complex plane diagram.', route: '/foundation', live: false },
  { id: 'pct-z-converter', label: '%Z to Ohms Converter', module: 'M02', desc: 'Enter MVA, kV, and %Z → compute transformer impedance in ohms.', route: '/transformers', live: false },
  { id: 'impedance-referral', label: 'Transformer Impedance Referral', module: 'M02', desc: 'Refer impedance across a transformer winding using the turns ratio.', route: '/transformers', live: false },
  { id: 'vector-group', label: 'Vector Group Phase Shift Viewer', module: 'M02', desc: 'Select Dyn11, YNyn0, etc. → display phase shift diagrams.', route: '/transformers', live: false },
  { id: 'sym-comp', label: 'Symmetrical Components Decomposer', module: 'M04', desc: 'Enter Va, Vb, Vc magnitudes/angles → compute V0, V1, V2.', route: '/symmetrical-components', live: false },
  { id: 'seq-phasor', label: 'Sequence Phasor Visualizer', module: 'M04', desc: 'Visualize all three sequence phasor sets simultaneously.', route: '/symmetrical-components', live: false },
  { id: 'fault-3ph', label: '3-Phase Fault Calculator', module: 'M06', desc: 'Enter Vbase and Z1 → compute Isc and fault MVA.', route: '/fault-analysis', live: false },
  { id: 'fault-slg', label: 'SLG Fault Calculator', module: 'M06', desc: 'Enter Z1, Z2, Z0 → compute Ia, Ib, Ic for single-line-to-ground fault.', route: '/fault-analysis', live: false },
  { id: 'fault-distance', label: 'Fault Current vs. Distance Plotter', module: 'M06', desc: 'Plot how fault current decreases along the feeder as impedance increases.', route: '/fault-analysis', live: false },
  { id: 'tcc-plotter', label: 'TCC Curve Plotter', module: 'M07', desc: 'Plot fuse and relay time-current curves on the same log-log chart.', route: '/protection', live: false },
  { id: 'relay-setting', label: 'Relay Setting Calculator', module: 'M07', desc: 'Enter CT ratio, load current, fault current → suggest pickup and time dial.', route: '/protection', live: false },
  { id: 'voltage-drop', label: 'Voltage Drop Calculator', module: 'M08', desc: 'Conductor type, length, load → compute percentage voltage drop.', route: '/distribution-design', live: false },
  { id: 'feeder-capacity', label: 'Feeder Capacity Utilization', module: 'M08', desc: 'Enter load profile → flag overloads and suggest conductor upgrades.', route: '/distribution-design', live: false },
];

const CalculatorsHub = () => (
  <div data-testid="calculators-hub-page">
    <div className="bg-gradient-to-br from-[#0B172A] to-slate-800 text-white px-4 sm:px-6 lg:px-8 py-12">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-semibold font-heading mb-3">Calculators Hub</h1>
        <p className="text-slate-300 text-lg max-w-2xl">
          All 20+ interactive engineering calculators in one place. Live calculators open directly — coming soon modules will be available in Phase 2.
        </p>
      </div>
    </div>

    <div className="px-4 sm:px-6 lg:px-8 py-10 max-w-4xl mx-auto">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {CALCULATORS.map(calc => (
          <div
            key={calc.id}
            data-testid={`calc-card-${calc.id}`}
            className={`relative border rounded-lg p-4 transition-colors ${
              calc.live
                ? 'bg-white dark:bg-slate-900 border-teal-200 dark:border-teal-800 hover:border-teal-400 dark:hover:border-teal-600'
                : 'bg-slate-50 dark:bg-slate-900/40 border-slate-200 dark:border-slate-800'
            }`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <span className="text-[10px] font-mono font-semibold text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/30 border border-teal-200 dark:border-teal-800 px-1.5 py-0.5 rounded">
                {calc.module}
              </span>
              {calc.live ? (
                <span className="text-[9px] font-bold bg-teal-600 text-white px-1.5 py-0.5 rounded-full">LIVE</span>
              ) : (
                <Lock className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
              )}
            </div>
            <h3 className={`font-semibold text-sm mb-1.5 font-heading ${calc.live ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-500'}`}>
              {calc.label}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-500 mb-3 leading-relaxed">{calc.desc}</p>
            {calc.live && (
              <Link
                to={`${calc.route}#calculators`}
                className="inline-flex items-center gap-1 text-xs font-medium text-teal-700 dark:text-teal-400 hover:underline"
              >
                Open calculator <ArrowRight className="h-3 w-3" />
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default CalculatorsHub;
